const TAG_ALIASES = {
  bespoke: ['bespoke'],
  highjewelry: ['highjewelry', 'highjewery', 'high-jewelry', 'high jewelry'],
};

function shuffle(items) {
  const result = [...items];

  for (let index = result.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [result[index], result[randomIndex]] = [result[randomIndex], result[index]];
  }

  return result;
}

function escapeHtml(value) {
  return String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function normalizeTag(tag) {
  return String(tag || '')
    .trim()
    .toLowerCase();
}

function matchesRelatedTag(productTags, relatedTag) {
  const aliases = TAG_ALIASES[relatedTag] || [relatedTag];
  const normalizedTags = productTags.map(normalizeTag);

  return aliases.some((alias) => normalizedTags.includes(alias));
}

function createRelatedProductCard(product) {
  const productImageSrc = product.featured_image || product.images?.[0]?.src;
  const productUrl = product.url || (product.handle ? `/products/${product.handle}` : '#');
  const variantPrice = product.variants?.[0]?.price;
  const productPrice = Number.isFinite(Number(variantPrice))
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
        Number(variantPrice)
      )
    : '';
  const featuredImage = productImageSrc
    ? `
      <img
        src="${escapeHtml(productImageSrc)}"
        alt="${escapeHtml(product.title)}"
        loading="lazy"
        sizes="(min-width: 990px) 20vw, 44vw"
        class="tooto-bespoke-work-detail__product-image"
      >
    `
    : `
      <div class="tooto-bespoke-work-detail__product-image tooto-bespoke-work-detail__image--placeholder"></div>
    `;

  return `
    <a
      class="tooto-bespoke-work-detail__product"
      href="${escapeHtml(productUrl)}"
      data-random-related-product
      data-related-hot="${product.tags.some((tag) => normalizeTag(tag) === 'hot') ? 'true' : 'false'}"
    >
      <div class="tooto-bespoke-work-detail__product-media">${featuredImage}</div>
      <span class="tooto-bespoke-work-detail__product-title">${escapeHtml(product.title)}</span>
      ${productPrice ? `<span class="tooto-bespoke-work-detail__product-price">${escapeHtml(productPrice)}</span>` : ''}
    </a>
  `;
}

async function fetchAllProducts() {
  const pageSize = 250;
  const maxPages = 20;
  const products = [];

  for (let page = 1; page <= maxPages; page += 1) {
    const response = await fetch(
      `/collections/all/products.json?limit=${pageSize}&page=${page}`
    );
    if (!response.ok) {
      throw new Error(`Failed to load products: ${response.status}`);
    }

    const payload = await response.json();
    const pageProducts = Array.isArray(payload.products)
      ? payload.products
      : [];

    products.push(...pageProducts);

    if (pageProducts.length < pageSize) {
      break;
    }
  }

  return products;
}

async function populateTagRelatedProducts(container) {
  if (!(container instanceof HTMLElement)) return;
  if (container.dataset.relatedLoaded === 'true') return;
  if (container.dataset.relatedSource !== 'tag') return;

  const relatedTag = normalizeTag(container.dataset.relatedTag);
  const currentProductHandle = container.dataset.currentProductHandle;

  if (!relatedTag || !currentProductHandle) return;

  try {
    const products = await fetchAllProducts();
    const relatedProducts = products.filter((product) => {
      if (!product?.handle || product.handle === currentProductHandle)
        return false;
      return matchesRelatedTag(product.tags || [], relatedTag);
    });

    if (relatedProducts.length === 0) {
      container.closest('.tooto-bespoke-work-detail__related')?.remove();
      return;
    }

    container.innerHTML = relatedProducts
      .map(createRelatedProductCard)
      .join('');
    container.dataset.relatedLoaded = 'true';
  } catch (error) {
    console.error('Failed to populate related products:', error);
    container.closest('.tooto-bespoke-work-detail__related')?.remove();
  }
}

function randomizeRelatedProducts(container) {
  if (!(container instanceof HTMLElement)) return;
  if (container.dataset.randomized === 'true') return;

  const cards = Array.from(
    container.querySelectorAll('[data-random-related-product]')
  );
  if (cards.length === 0) return;

  const visibleCount = Number.parseInt(
    container.dataset.randomRelatedVisibleCount || '4',
    10
  );
  const hotCards = shuffle(
    cards.filter((card) => card.dataset.relatedHot === 'true')
  );
  const normalCards = shuffle(
    cards.filter((card) => card.dataset.relatedHot !== 'true')
  );
  const prioritizedCards = [
    ...hotCards.slice(0, visibleCount),
    ...normalCards.slice(
      0,
      Math.max(visibleCount - Math.min(hotCards.length, visibleCount), 0)
    ),
  ];
  const remainingCards = [...hotCards, ...normalCards].filter(
    (card) => !prioritizedCards.includes(card)
  );
  const randomizedCards = [...prioritizedCards, ...remainingCards];

  randomizedCards.forEach((card, index) => {
    card.hidden = index >= visibleCount;
    container.appendChild(card);
  });

  container.dataset.randomized = 'true';
}

async function initializeRandomRelatedProducts(root = document) {
  const containers = root.matches?.('[data-random-related-products]')
    ? [root]
    : Array.from(root.querySelectorAll('[data-random-related-products]'));

  for (const container of containers) {
    await populateTagRelatedProducts(container);
    randomizeRelatedProducts(container);
  }
}

function watchRelatedProductContainers() {
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        initializeRandomRelatedProducts(node);
      }
    }
  });

  observer.observe(document.documentElement, {
    childList: true,
    subtree: true,
  });
}

function bootRelatedProducts() {
  initializeRandomRelatedProducts();
  watchRelatedProductContainers();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootRelatedProducts, {
    once: true,
  });
} else {
  bootRelatedProducts();
}
